// ./src/components/features/PlanSelector/PlanSelector.tsx
import { useEffect } from "react";
import {
  Card,
  Group,
  Text,
  Badge,
  Stack,
  Grid,
  Loader,
  Alert,
  Button,
  Box,
  Title,
  List,
  ThemeIcon,
  Divider,
  Center,
} from "@mantine/core";
import {
  IconCheck,
  IconX,
  IconCrown,
  IconInfoCircle,
  IconRefresh,
} from "@tabler/icons-react";
import { useAppPlans } from "@/hooks";
import { AppPlan, AppPlanPrice } from "@/utils/api";

export interface PlanSelectorProps {
  /** Current user's plan code */
  currentPlanId?: string | null | undefined;

  /** Date when trial ends (null if user is on paid plan) */
  trialEndsAt?: Date | null;

  /** Callback when user selects a plan */
  onChange: (plan: AppPlan, price: AppPlanPrice) => void;

  /** Show only active plans */
  showActiveOnly?: boolean;

  /** Loading state override */
  loading?: boolean;
}

const plansStatus = 100;

export const PlanSelector = ({
  currentPlanId,
  trialEndsAt,
  onChange,
  showActiveOnly = true,
  loading: externalLoading,
}: PlanSelectorProps) => {
  const { appPlans, loading, lastError, list, clearError } = useAppPlans({
    pageSize: 50, // Load all plans at once
    initialFilters: showActiveOnly ? { status: [plansStatus] } : {},
    autoLoad: false,
  });

  const isLoading = externalLoading || loading;
  const isOnTrial = trialEndsAt !== null && trialEndsAt !== undefined;
  const canSelectCurrentPlan = isOnTrial;

  // Load plans on component mount
  useEffect(() => {
    const filters = showActiveOnly ? { status: [plansStatus] } : {};
    list(filters, { page: 1, pageSize: 50 }, undefined, true);
  }, [list, showActiveOnly]);

  // Helper to parse feature value based on type
  const parseFeatureValue = (
    value: string | undefined,
    type: string | undefined | null
  ): any => {
    if (!value || !type) return null;

    try {
      switch (type) {
        case "boolean":
          return value.toLowerCase() === "true";
        case "integer":
          return parseInt(value, 10);
        case "decimal":
          return parseFloat(value);
        case "string[]":
        case "integer[]":
        case "decimal[]":
        case "boolean[]":
          return JSON.parse(value);
        case "string":
        default:
          return value;
      }
    } catch {
      return value;
    }
  };

  // Helper to render feature value
  const renderFeatureValue = (
    value: string | undefined,
    type: string | undefined | null
  ): string => {
    if (!value) return "—";

    const parsedValue = parseFeatureValue(value, type);

    if (parsedValue === null || parsedValue === undefined) return "—";

    if (typeof parsedValue === "boolean") {
      return parsedValue ? "Yes" : "No";
    }

    if (Array.isArray(parsedValue)) {
      return parsedValue.join(", ");
    }

    return parsedValue.toString();
  };

  // Helper to get feature icon
  const getFeatureIcon = (
    value: string | undefined,
    type: string | undefined | null
  ) => {
    if (!value) return <IconX size={16} />;

    const parsedValue = parseFeatureValue(value, type);

    if (type === "boolean") {
      return parsedValue ? (
        <IconCheck size={16} color="var(--mantine-color-green-6)" />
      ) : (
        <IconX size={16} color="var(--mantine-color-red-6)" />
      );
    }

    return <IconCheck size={16} color="var(--mantine-color-blue-6)" />;
  };

  // Check if plan is current
  const isCurrentPlan = (planCode: string | undefined) =>
    planCode === currentPlanId;

  // Check if plan can be selected
  const canSelectPlan = (planCode: string | undefined) => {
    const isCurrent = isCurrentPlan(planCode);
    return !isCurrent || canSelectCurrentPlan;
  };

  // Get plan card variant based on status
  const getPlanCardProps = (plan: AppPlan) => {
    const isCurrent = isCurrentPlan(plan.planCode);
    const isPopular = plan.isPlanFeatured;

    if (isCurrent) {
      return {
        withBorder: true,
        style: { borderColor: "var(--mantine-color-blue-6)", borderWidth: 2 },
      };
    }

    if (isPopular) {
      return {
        withBorder: true,
        style: { borderColor: "var(--mantine-color-orange-6)", borderWidth: 2 },
      };
    }

    return { withBorder: true };
  };

  if (isLoading) {
    return (
      <Center h={400}>
        <Stack align="center" gap="md">
          <Loader size="lg" />
          <Text c="dimmed">Loading plans...</Text>
        </Stack>
      </Center>
    );
  }

  if (lastError) {
    return (
      <Alert
        variant="light"
        color="red"
        title="Error loading plans"
        icon={<IconInfoCircle size={16} />}
      >
        <Stack gap="sm">
          <Text>{lastError}</Text>
          <Group gap="xs">
            <Button
              variant="light"
              size="xs"
              leftSection={<IconRefresh size={14} />}
              onClick={() =>
                list(
                  showActiveOnly ? { status: [100] } : {},
                  { page: 1, pageSize: 50 },
                  undefined,
                  true
                )
              }
            >
              Retry
            </Button>
            <Button
              variant="subtle"
              size="xs"
              leftSection={<IconX size={14} />}
              onClick={clearError}
            >
              Dismiss
            </Button>
          </Group>
        </Stack>
      </Alert>
    );
  }

  if (appPlans.length === 0) {
    return (
      <Center h={200}>
        <Stack align="center" gap="md">
          <Text c="dimmed" size="lg">
            No plans available
          </Text>
          <Button
            variant="light"
            leftSection={<IconRefresh size={16} />}
            onClick={() =>
              list(
                showActiveOnly ? { status: [100] } : {},
                { page: 1, pageSize: 50 },
                undefined,
                true
              )
            }
          >
            Refresh
          </Button>
        </Stack>
      </Center>
    );
  }

  return (
    <Box>
      <Grid>
        {appPlans.map((plan) => {
          const isCurrent = isCurrentPlan(plan.planCode);
          const isPopular = plan.isPlanFeatured;
          const cardProps = getPlanCardProps(plan);
          const planPrice = plan.prices?.find(
            (p) => p.recurrence === "MONTHLY"
          );
          const monthlyPrice = planPrice?.price || 0;

          if (!planPrice) {
            return null;
          }

          return (
            <Grid.Col key={plan.id} span={{ base: 12, sm: 6, lg: 4 }}>
              <Card
                {...cardProps}
                shadow="sm"
                padding="lg"
                radius="md"
                h="100%"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  position: "relative",
                  ...cardProps.style,
                }}
              >
                {/* Popular badge */}
                {isPopular && !isCurrent && (
                  <Badge
                    variant="filled"
                    color="orange"
                    leftSection={<IconCrown size={12} />}
                    style={{
                      position: "absolute",
                      top: 10,
                      right: 16,
                      zIndex: 1,
                    }}
                  >
                    Popular
                  </Badge>
                )}

                {/* Current plan badge */}
                {isCurrent && (
                  <Badge
                    variant="filled"
                    color="blue"
                    leftSection={<IconCheck size={12} />}
                    style={{
                      position: "absolute",
                      top: 10,
                      right: 16,
                      zIndex: 1,
                    }}
                  >
                    Current Plan
                  </Badge>
                )}

                <Stack gap="md" style={{ flex: 1 }}>
                  {/* Plan header */}
                  <Stack gap="xs">
                    <Title order={3} fw={600}>
                      {plan.planName}
                    </Title>
                    <Text c="dimmed" size="sm">
                      {plan.planDescription}
                    </Text>
                  </Stack>

                  {/* Pricing */}
                  <Box>
                    <Group align="baseline" gap="xs">
                      <Text size="xl" fw={700} c="blue">
                        ${monthlyPrice}
                      </Text>
                      <Text c="dimmed" size="sm">
                        /month
                      </Text>
                    </Group>
                  </Box>

                  <Divider />

                  {/* Features */}
                  <Stack gap="xs" style={{ flex: 1 }}>
                    <Text fw={500} size="sm">
                      Features
                    </Text>

                    <List spacing="xs" size="sm" style={{ listStyle: "none" }}>
                      {plan.planFeatures?.map((planFeature) => {
                        const feature = planFeature.feature;
                        if (!feature) return null;

                        const displayName =
                          feature.featureName || feature.featureCode || "";
                        const value = renderFeatureValue(
                          planFeature.featureValue,
                          feature.featureValueType
                        );
                        const icon = getFeatureIcon(
                          planFeature.featureValue,
                          feature.featureValueType
                        );
                        const isDescription =
                          value !== "Yes" && value !== "No" && value !== "—";
                        const isUnlimited = value === "—" ? "unlimited" : "";

                        return (
                          <List.Item
                            key={feature.id}
                            icon={
                              <ThemeIcon variant="light" size="sm" radius="xl">
                                {icon}
                              </ThemeIcon>
                            }
                            styles={{
                              itemWrapper: {
                                alignItems: "flex-start",
                              },
                            }}
                          >
                            <Group justify="space-between" gap="sm">
                              <Text size="sm">{displayName}</Text>
                              {(isDescription || isUnlimited) && (
                                <Text size="sm" fw={600} c="green">
                                  {isUnlimited || value}
                                </Text>
                              )}
                            </Group>
                          </List.Item>
                        );
                      })}
                    </List>
                  </Stack>

                  {/* Action button */}
                  <Button
                    fullWidth
                    variant={isCurrent ? "light" : "filled"}
                    color={isCurrent ? "blue" : "blue"}
                    disabled={!canSelectPlan(plan.planCode)}
                    onClick={() => onChange(plan, planPrice)}
                    size="md"
                  >
                    {isCurrent
                      ? canSelectCurrentPlan
                        ? "Continue with this plan"
                        : "Current Plan"
                      : "Select Plan"}
                  </Button>

                  {/* Additional info for current plan */}
                  {isCurrent && !canSelectCurrentPlan && (
                    <Text size="xs" c="dimmed" ta="center">
                      You're currently on this plan
                    </Text>
                  )}
                </Stack>
              </Card>
            </Grid.Col>
          );
        })}
      </Grid>
    </Box>
  );
};
